import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import CallCenterNav from "../../components/call-center/CallCenterNav";
import { useCampaigns } from "../../hooks/useCampaigns";
import { useTickets } from "../../hooks/useTickets";

const percent = (value, total) => {
  const num = Number(value || 0);
  const den = Number(total || 0);
  if (!den) return 0;
  return Math.max(0, Math.min(100, Math.round((num / den) * 100)));
};

const MetricCard = ({ title, value, subtitle, accent = "from-blue-500 to-indigo-500" }) => (
  <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`} />
    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</div>
    <div className="mt-2 text-3xl font-black text-gray-800">{value}</div>
    {subtitle ? <div className="mt-1 text-xs text-gray-500">{subtitle}</div> : null}
  </div>
);

const CallCenterAnalyticsPage = () => {
  const ticketsApi = useTickets();
  const campaignsApi = useCampaigns();

  const [teamProgress, setTeamProgress] = useState(null);
  const [campaignDashboard, setCampaignDashboard] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [team, campaign] = await Promise.all([
        ticketsApi.teamProgress(),
        campaignsApi.getDashboard(),
      ]);
      setTeamProgress(team || null);
      setCampaignDashboard(campaign || null);
    } catch (error) {
      toast.error(error.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const calledRate = useMemo(
    () => percent(teamProgress?.totalCalledTargets, teamProgress?.totalTargets),
    [teamProgress]
  );
  const paidRate = useMemo(
    () => percent(teamProgress?.totalPaidTargets, teamProgress?.totalTargets),
    [teamProgress]
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <header className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Call Center Analytics</h2>
              <p className="text-gray-500">Team performance and campaign intelligence in one place</p>
            </div>
            <button
              type="button"
              onClick={loadAnalytics}
              disabled={loading}
              className="h-10 px-4 rounded-lg bg-[#236DB4] text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Refreshing..." : "Refresh Analytics"}
            </button>
          </div>
          <div className="mt-4">
            <CallCenterNav />
          </div>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800">Team Progress</h3>
              <p className="text-xs text-gray-500">Ticket target execution by member</p>
            </div>
            {teamProgress ? (
              <div className="space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard title="Total Targets" value={teamProgress.totalTargets ?? 0} accent="from-sky-500 to-blue-600" />
                  <MetricCard title="Called Targets" value={teamProgress.totalCalledTargets ?? 0} accent="from-amber-400 to-orange-500" />
                  <MetricCard title="Paid Targets" value={teamProgress.totalPaidTargets ?? 0} accent="from-emerald-500 to-green-600" />
                  <MetricCard title="Recovery" value={teamProgress.totalRecoveryAmount ?? 0} accent="from-violet-500 to-purple-600" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Called Coverage</span>
                      <span>{calledRate}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full bg-amber-500" style={{ width: `${calledRate}%` }} />
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Paid Conversion</span>
                      <span>{paidRate}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${paidRate}%` }} />
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-gray-100 text-gray-500">
                      <tr>
                        <th className="py-2 text-left">Member</th>
                        <th className="py-2 text-left">Assigned</th>
                        <th className="py-2 text-left">Called</th>
                        <th className="py-2 text-left">Paid</th>
                        <th className="py-2 text-left">Pending</th>
                        <th className="py-2 text-left">Recovery</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(teamProgress.members) ? teamProgress.members : []).map((member) => (
                        <tr key={member.userId} className="border-b border-gray-50">
                          <td className="py-2 font-medium text-gray-700">{member.userName || `User ${member.userId}`}</td>
                          <td className="py-2">{member.assignedTargets ?? 0}</td>
                          <td className="py-2">{member.calledTargets ?? 0}</td>
                          <td className="py-2">{member.paidTargets ?? 0}</td>
                          <td className="py-2">{member.pendingTargets ?? 0}</td>
                          <td className="py-2">{member.recoveryAmount ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No team progress data available.</div>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800">Campaign Analytics</h3>
              <p className="text-xs text-gray-500">Live campaign throughput and outcome quality</p>
            </div>
            {campaignDashboard ? (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <MetricCard title="Total Calls" value={campaignDashboard.totalCalls ?? 0} accent="from-cyan-500 to-blue-600" />
                  <MetricCard title="Answer Rate" value={`${campaignDashboard.answeredPercentage ?? 0}%`} accent="from-teal-500 to-emerald-600" />
                  <MetricCard title="Success Rate" value={`${campaignDashboard.successPercentage ?? 0}%`} accent="from-indigo-500 to-violet-600" />
                  <MetricCard title="Recovery" value={campaignDashboard.recoveryAmount ?? 0} accent="from-orange-500 to-amber-600" />
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs text-gray-600 mb-2">Campaign Performance Snapshot</div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Answered %</span>
                        <span>{campaignDashboard.answeredPercentage ?? 0}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-emerald-500"
                          style={{ width: `${Math.max(0, Math.min(100, Number(campaignDashboard.answeredPercentage || 0)))}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Success %</span>
                        <span>{campaignDashboard.successPercentage ?? 0}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-indigo-500"
                          style={{ width: `${Math.max(0, Math.min(100, Number(campaignDashboard.successPercentage || 0)))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No campaign analytics data available.</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default CallCenterAnalyticsPage;
